import { Text, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui-kit'
import { type MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import clsx from 'clsx'
import { PropsWithChildren } from 'react'
import { type Mission } from '../api/models'

export const STATUS_TEXT_MAP: Record<string, MessageDescriptor> = {
  null: msg`New`,
  IN_PROGRESS: msg`In Progress`,
  COMPLETED: msg`Completed`,
  SUBMITTED: msg`Validating`,
  FAILED: msg`Rejected`
}
export type BadgeVariant = 'success' | 'yellow' | 'warning' | 'error' | 'default'

export function getBadgeVariant(mission: {
  type: Mission['type']
  view: { latestSubmissionStatus: string | null; nextSubmissionAvailable: boolean }
}): BadgeVariant {
  const { latestSubmissionStatus, nextSubmissionAvailable } = mission.view
  if (latestSubmissionStatus === 'FAILED') return 'error'
  if (latestSubmissionStatus === 'COMPLETED' && nextSubmissionAvailable && mission.type === 'WEBSITE_LOGIN')
    return 'error'
  if (latestSubmissionStatus === 'IN_PROGRESS' || latestSubmissionStatus === 'SUBMITTED') return 'warning'
  if (nextSubmissionAvailable || latestSubmissionStatus === 'COMPLETED') return 'success'
  return 'default'
}

export const BUTTON_TEXT_MAP: Record<string, MessageDescriptor> = {
  null: msg`Earn`,
  IN_PROGRESS: msg`Continue`,
  COMPLETED: msg`Manage`,
  SUBMITTED: msg`Validating...`,
  FAILED: msg`Failed`
}

type MissionBadgeProps = PropsWithChildren<{
  variant?: BadgeVariant
  className?: string
  tooltip?: string
}>

export const BadgeContent = ({
  children,
  variant,
  className
}: {
  children: React.ReactNode
  variant: MissionBadgeProps['variant']
  className?: string
}) => {
  const variantClass = {
    success: 'text-green border-green/20',
    yellow: 'text-yellow border-yellow/20',
    warning: 'text-[var(--colorOrange)] border-[color:var(--colorOrange)]/20',
    error: 'text-[var(--colorError)] border-[color:var(--colorError)]/20',
    default: 'text-[var(--colorGray80to90)] border-[color:var(--colorGray80to90)]/20'
  }[variant ?? 'default']

  return (
    <div
      className={clsx(
        'flex items-center gap-2 rounded-lg border border-solid p-2 whitespace-nowrap',
        variantClass,
        className
      )}
    >
      <Text type="body2-regular" className={clsx('flex items-center gap-1')}>
        {children}
      </Text>
    </div>
  )
}

export const MissionBadge = ({ children, variant = 'default', className, tooltip }: MissionBadgeProps) => {
  return (
    <div className={className}>
      {tooltip ? (
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <BadgeContent variant={variant} className={className}>
                  {children}
                </BadgeContent>
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[320px] text-[12px]/[16px]!" sideOffset={8}>
              <Text type="body3-regular" className="">
                {tooltip}
              </Text>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <BadgeContent variant={variant}>{children}</BadgeContent>
      )}
    </div>
  )
}
